import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Upload, FileText, Edit, Trash2, Loader2, Check, X, ExternalLink } from 'lucide-react';

const AssessmentFormsPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [editingName, setEditingName] = useState(null);
    const [newName, setNewName] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => { fetchForms(); }, []);

    const fetchForms = async () => {
        try {
            const res = await axios.get(`${API}/admin/assessment-forms`, { headers: getAuthHeaders() });
            setForms(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/admin/assessment-forms/upload`, fd, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
                timeout: 120000
            });
            toast.success('Assessment form extracted successfully');
            fetchForms();
            // Navigate to form builder for the newly created form
            if (res.data?.id) navigate(`/assessment-forms/${res.data.id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to extract form');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRename = async (formId) => {
        if (!newName.trim()) return;
        try {
            await axios.put(`${API}/admin/assessment-forms/${formId}`, { form_name: newName.trim() }, { headers: getAuthHeaders() });
            toast.success('Form renamed');
            setEditingName(null);
            fetchForms();
        } catch (e) { toast.error('Failed'); }
    };

    const handleDelete = async (formId, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this assessment form?')) return;
        try {
            await axios.delete(`${API}/admin/assessment-forms/${formId}`, { headers: getAuthHeaders() });
            toast.success('Form deleted');
            fetchForms();
        } catch (e) { toast.error('Failed'); }
    };

    const handlePublish = async (formId, status, e) => {
        e.stopPropagation();
        try {
            await axios.put(`${API}/admin/assessment-forms/${formId}`, { status }, { headers: getAuthHeaders() });
            toast.success(`Form ${status === 'published' ? 'published' : 'set to draft'}`);
            fetchForms();
        } catch (e) { toast.error('Failed'); }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="assessment-forms-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Assessment Forms</h1>
                        <p className="text-gray-500 mt-1">Upload documents for AI extraction or build forms manually</p>
                    </div>
                    <div className="flex gap-2">
                        <input ref={fileInputRef} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" onChange={handleUpload} className="hidden" id="assessment-upload" data-testid="assessment-file-input" />
                        <label htmlFor="assessment-upload">
                            <Button asChild disabled={uploading} className="cursor-pointer">
                                <span data-testid="upload-assessment-btn">
                                    {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Extracting...</>) : (<><Upload className="w-4 h-4 mr-2" />Upload Document</>)}
                                </span>
                            </Button>
                        </label>
                    </div>
                </div>

                {uploading && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                        <p className="text-sm text-blue-700">AI is analyzing your document and extracting the assessment structure. This may take a moment...</p>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-3">{[1, 2].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
                ) : forms.length > 0 ? (
                    <div className="space-y-3">
                        {forms.map(form => (
                            <div key={form.id} onClick={() => navigate(`/assessment-forms/${form.id}`)}
                                className="bg-white rounded-lg border border-gray-200 p-4 transition-all cursor-pointer hover:border-ica-gold/50 hover:shadow-md group"
                                data-testid={`form-card-${form.id}`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        <div className="w-10 h-10 bg-ica-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FileText className="w-5 h-5 text-ica-navy" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            {editingName === form.id ? (
                                                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                                    <Input value={newName} onChange={e => setNewName(e.target.value)} className="h-8 text-sm" autoFocus
                                                        onKeyDown={e => { if (e.key === 'Enter') handleRename(form.id); if (e.key === 'Escape') setEditingName(null); }} />
                                                    <Button size="sm" variant="ghost" onClick={() => handleRename(form.id)} className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-600 dark:text-emerald-400" title="Confirm Rename"><Check className="w-4 h-4" /></Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditingName(null)} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300" title="Cancel"><X className="w-4 h-4" /></Button>
                                                </div>
                                            ) : (
                                                <p className="font-medium text-gray-900 truncate group-hover:text-ica-navy transition-colors">{form.form_name}</p>
                                            )}
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${form.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                                                    {form.status}
                                                </span>
                                                <span className="text-xs text-gray-400">{formatDate(form.created_at)}</span>
                                                <span className="text-xs text-gray-400">{form.form_data?.sections?.length || 0} sections</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold"
                                            onClick={(e) => { e.stopPropagation(); setEditingName(form.id); setNewName(form.form_name); }} data-testid={`rename-form-${form.id}`} title="Rename Form">
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold"
                                            onClick={(e) => handlePublish(form.id, form.status === 'published' ? 'draft' : 'published', e)} title={form.status === 'published' ? 'Unpublish' : 'Publish'}>
                                            {form.status === 'published' ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                                            onClick={(e) => handleDelete(form.id, e)} title="Delete Form">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                        <ExternalLink className="w-4 h-4 text-gray-300 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                        <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                        <h3 className="text-base font-semibold text-gray-700 mb-1">No assessment forms</h3>
                        <p className="text-gray-500">Upload a PDF or DOCX to extract assessment structure via AI</p>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AssessmentFormsPage;
